import { LogicalExpressionParser } from "../../../../logic/LogicalExpressionParser";
import { isNull, isNumber, isString } from "../../../../util/TypeUtil";
import IStoryParser from "./IStoryParser"
import StoryFactory from "../../engine/model/StoryFactory"
import ContentFactory from "../../../content/ContentFactory";
/**
 * The StoryParser is a class that can serialize and deserialize a story. It
 * depends on some assumptions that are in the framework:
 * 
 * 1) All ILogicalStatements are ranj.logic.LogicalExpression
 * 2) All data field must be of type IObjectConvertable
 * 
 * @constructor
 * @returns {undefined}
 */
export default class ObjStoryParser extends IStoryParser {

    constructor(languagePack) {
        super();
        this._languagePack = languagePack;
    }
    /**
         * Serialize a Story
         * @param {Story} story
         * @returns {*} Serialized data for the Story
         */
    serializeStory(story) {
        var result = {};
        //Serialize properties
        result.data = story.data;

        //Serialize sotryArcs
        result.storyArcs = [];
        var storyArcItr = story.getStoryArcs();
        while (storyArcItr.hasNext()) {
            var storyArc = storyArcItr.next();
            result.storyArcs.add(this.serializeStoryArcObj(storyArc));
        }
        return result;
    }
    /**
         * Serialize a StoryArc
         * @param {StoryArc} storyArc
         * @returns {*}
         */
    serializeStoryArcObj(storyArc) {
        var result = {};
        //Serialize properties
        result.id = storyArc.id;
        result.data = this.serializeData(storyArc.getData());
        result.metadata = this.serializeMetadata(storyArc.getMetadata());
        result.unlockCondition = this.serializeLogicalExpression(storyArc.getUnlockCondition());
        result.availableCondition = this.serializeLogicalExpression(storyArc.getAvailableCondition());
        result.isEnd = storyArc.isEnd();
        result.isRetained = storyArc.isRetained();
        result.label = storyArc.getLabel();

        //Serialize storynodes
        result.storyNodes = [];
        var storyNodeIterator = storyArc.getStoryNodes();
        while (storyNodeIterator.hasNext()) {
            var storyNode = storyNodeIterator.next();
            result.storyNodes.push(this.serializeStoryNodeObj(storyNode));
        }
        return result;
    }
    /**
         * Serialize a StoryNode
         * @param {type} storyNode
         * @returns {*}
         */
    serializeStoryNodeObj(storyNode) {
        var result = {};
        //Serialize properties
        result.id = storyNode.id;
        result.data = this.serializeData(storyNode.getData());
        result.metadata = this.serializeMetadata(storyNode.getMetadata());
        result.unlockCondition = this.serializeLogicalExpression(storyNode.getUnlockCondition());
        result.availableCondition = this.serializeLogicalExpression(storyNode.getAvailableCondition());
        result.label = storyNode.getLabel();
        result.isEnd = storyNode.isEnd();
        result.isRetained = storyNode.isRetained();
        return result;
    }
    /**
         * Serializes a LogicalExpression to a String
         * @param {LogicalExpression} statement
         * @returns {String}
         */
    serializeLogicalExpression(statement) {
        if (isNull(statement)) {
            return null;
        }
        return LogicalExpressionParser.serializeLogicalExpression(statement);
    }
    /**
         * Serializes data on StoryNodes and StoryArc. Assumes data to be String, number, null or
         * of type IContent.
         * @param {type} data
         * @returns {@exp;data@call;convertToObject}
         */
    serializeData(data) {
        if (isString(data) || isNumber(data) || isNull(data)) {
            return data;
        } else {
            //FIXME: convertToObject is not implemented yet in contenttypes
            return data.convertToObject();
        }
    }
    /**
         * Serializes metadata
         * @param {type} data
         * @returns {*}
         */
    serializeMetadata(data) {
        return data;
    }
    /**
         * Parse data and deserialize a Story from it
         * @param {*} obj Data to be processed
         * @returns {Story} The Story
         */
    parseStory(obj) {
        var story = StoryFactory.createStory();
        //parse properties
        story.data = obj.data;

        if (Array.isArray(obj.storyArcs)) {
            for (var i = 0; i < obj.storyArcs.length; i++) {
                var storyArc = story.insertStoryArc(null, null, null, false, obj.storyArcs[i].id);
                this.parseStoryArcObj(obj.storyArcs[i], storyArc);
            }
        }
        return story;

    }
    /**
         * Parses a StoryArc from data
         * @param {*} obj The input data
         * @param {StoryArc} storyArc The StoryArc to set properties on
         * @returns {undefined}
         */
    parseStoryArcObj(obj, storyArc) {
        //parse properties
        storyArc.setData(this.parseData(obj.data));
        storyArc.setMetadata(this.parseData(obj.metadata));
        storyArc.setUnlockCondition(this.parseLogicalExpression(obj.unlockCondition));
        storyArc.setAvailableCondition(this.parseLogicalExpression(obj.availableCondition));
        storyArc.setEnd(obj.isEnd);
        storyArc.setRetained(obj.isRetained);
        storyArc.setLabel(obj.label);


        if (Array.isArray(obj.storyNodes)) {
            for (var i = 0; i < obj.storyNodes.length; i++) {
                var storyNode = storyArc.insertStoryNode(null, null, null, obj.storyNodes[i].id);
                this.parseStoryNodeObj(obj.storyNodes[i], storyNode);
            }
        }
    }
    /**
         * Parses a StoryNode from data
         * @param {*} obj The input data
         * @param {StoryNode} storyNode The StoryNode to set properties on
         * @returns {undefined}
         */
    parseStoryNodeObj(obj, storyNode) {
        //parse properties
        storyNode.setData(this.parseData(obj.data));
        storyNode.setMetadata(this.parseData(obj.metadata));
        storyNode.setUnlockCondition(this.parseLogicalExpression(obj.unlockCondition));
        storyNode.setAvailableCondition(this.parseLogicalExpression(obj.availableCondition));
        storyNode.setEnd(obj.isEnd);
        storyNode.setRetained(obj.isRetained);
        storyNode.setLabel(obj.label);
    }
    /**
         * Parses a string to a LogicalExpression
         * @param {String} obj The string to parse
         * @returns {LogicalExpression}
         */
    parseLogicalExpression(obj) {
        if (isString(obj)) {
            return LogicalExpressionParser.parseLogicalExpression(obj);
        }
        return null;
    }
    /**
         * Parses a data field. Returns basic types as is, assumes that data is IContent for
         * complex data.
         * @param {*} obj Input data
         * @returns {*}
         */
    parseData(obj) {
        if (isString(obj) || isNumber(obj) || isNull(obj)) {
            return obj;
        } else {
            var content = ContentFactory.getContentFromObject(obj, this._languagePack);
            if (content === null || content === undefined) {
                throw "Unknown content type in story file. Saving the story file will result in loss of content. Please upgrade your story editor!";
            }
            return content;
        }
    }
}
